// src/components/chat/ChatHistory.tsx
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { Box, Typography, useTheme } from "@mui/material";
import React, { useEffect, useRef } from "react";
import ChatMessage, { ChatMessageType } from "./ChatMessage";
import { useTranslation } from "../../i18n";

interface ChatHistoryProps {
  messages: ChatMessageType[];
  onDocumentClick?: (url: string) => void;
}

const ChatHistory: React.FC<ChatHistoryProps> = ({
  messages,
  onDocumentClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        overflowY: "auto",
        px: 2.5,
        py: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        backgroundColor: "transparent",
        // Custom scrollbar styling - unified using theme divider color
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.divider,
          borderRadius: "3px",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        },
      }}
    >
      {messages.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            gap: 1.5,
            opacity: 0.5,
            py: 4,
          }}
        >
          <ChatBubbleOutlineIcon
            sx={{
              fontSize: 40,
              color: theme.custom?.contentText?.muted || theme.palette.text.disabled,
            }}
          />
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" sx={{ mb: 0.5, fontSize: "0.875rem", color: theme.custom?.contentText?.secondary || theme.palette.text.secondary }}>
              No messages yet
            </Typography>
            <Typography variant="caption" sx={{ fontSize: "0.75rem", color: theme.custom?.contentText?.muted || theme.palette.text.disabled }}>
              {t("chat.start_typing_hint", "Start typing, upload files, or record a voice note")}
            </Typography>
          </Box>
        </Box>
      ) : (
        messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            onDocumentClick={onDocumentClick}
          />
        ))
      )}
    </Box>
  );
};

export default ChatHistory;
